import React, { useState, useEffect, useRef } from 'react';
import { 
  TrendingUp, 
  TrendingDown, 
  PoundSterling, 
  BarChart3, 
  Wallet, 
  Target,
  ArrowUpRight,
  ArrowDownRight,
  Clock,
  AlertCircle
} from 'lucide-react';
import api from '../../services/api';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
// import FundManagement from '../../components/UI/FundManagement';

const fetchLivePrice = async (symbol) => {
  const baseSymbol = symbol.endsWith('.L') ? symbol.replace('.L', '') : symbol;
  const url = `${process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1'}/market-data/company-profile/?symbol=${baseSymbol}`;
  try {
    const res = await fetch(url);
    const data = await res.json();
    if (data && data.price !== undefined) {
      return {
        price: data.price,
        previous_close: data.previous_close,
        today_change: data.today_change,
        today_change_percent: data.today_change_percent,
      };
    }
  } catch (e) {}
  return null;
};

// Utility to check if UK market is open (Mon-Fri, 8:00-16:30 London time)
function isUkMarketOpen() {
  const now = new Date();
  // Convert to London time
  const utc = now.getTime() + now.getTimezoneOffset() * 60000;
  // London is UTC+1 during BST (last Sunday in March to last Sunday in October), UTC+0 otherwise
  const startBST = new Date(now.getFullYear(), 2, 31); // March 31
  startBST.setDate(31 - (startBST.getDay() || 7) + 7); // last Sunday in March
  const endBST = new Date(now.getFullYear(), 9, 31); // October 31
  endBST.setDate(31 - (endBST.getDay() || 7)); // last Sunday in October
  let offset = 0;
  if (now >= startBST && now < endBST) {
    offset = 1; // BST
  }
  const london = new Date(utc + 3600000 * offset);
  const day = london.getDay();
  const hour = london.getHours();
  const minute = london.getMinutes();
  // Market open Mon-Fri, 8:00-16:30
  if (day === 0 || day === 6) return false;
  if (hour < 8 || (hour === 16 && minute > 30) || hour > 16) return false;
  return !(hour === 16 && minute > 30);
}

const HoldingCard = ({ holding, formatCurrency, onLiveUpdate }) => {
  const [live, setLive] = useState({
    price: holding.current_price,
    previous_close: holding.previous_close,
    today_change: holding.today_change,
    today_change_percent: holding.today_change_percent,
  });
  const [calc, setCalc] = useState({
    current_value: holding.current_value,
    unrealized_gain_loss: holding.unrealized_gain_loss,
    unrealized_gain_loss_percent: holding.unrealized_gain_loss_percent,
  });

  useEffect(() => {
    let isMounted = true;
    const updateLive = async () => {
      // Only update if market is open
      if (!isUkMarketOpen()) {
        // When market is closed, use the original data from backend
        setLive({
          price: holding.current_price,
          previous_close: holding.previous_close,
          today_change: holding.today_change,
          today_change_percent: holding.today_change_percent,
        });
        setCalc({
          current_value: holding.current_value,
          unrealized_gain_loss: holding.unrealized_gain_loss,
          unrealized_gain_loss_percent: holding.unrealized_gain_loss_percent,
        });
        return;
      }
      
      const data = await fetchLivePrice(holding.symbol);
      if (data && isMounted) {
        setLive(data);
        // Recalculate values
        const current_price = data.price;
        const current_value = holding.quantity * current_price;
        const unrealized_gain_loss = current_value - (holding.quantity * holding.average_price);
        const unrealized_gain_loss_percent = (holding.quantity * holding.average_price) > 0 ? (unrealized_gain_loss / (holding.quantity * holding.average_price)) * 100 : 0;
        const newCalc = { current_value, unrealized_gain_loss, unrealized_gain_loss_percent };
        setCalc(newCalc);
        if (onLiveUpdate) {
          onLiveUpdate(holding.symbol, data, newCalc);
        }
      }
    };
    updateLive();
    const interval = setInterval(updateLive, 25000);
    return () => { isMounted = false; clearInterval(interval); };
  }, [holding.symbol, holding.quantity, holding.average_price, holding.current_price, holding.current_value, holding.unrealized_gain_loss, holding.unrealized_gain_loss_percent, holding.today_change, holding.today_change_percent]);

  const isPositive = (calc.unrealized_gain_loss || 0) >= 0;
  const isTodayPositive = live.today_change > 0;

  return (
         <div className="group relative bg-white dark:bg-gray-800 overflow-hidden shadow-lg rounded-xl border border-gray-200 dark:border-gray-700 hover:shadow-xl transition-all duration-300 hover:scale-[1.02] min-h-[240px]">
       {/* Gradient overlay */}
       <div className={`absolute inset-0 bg-gradient-to-br ${
         isPositive 
           ? 'from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20' 
           : 'from-red-50 to-rose-50 dark:from-red-900/20 dark:to-rose-900/20'
       } opacity-0 group-hover:opacity-100 transition-opacity duration-300`} />
       
       <div className="relative p-4 h-full flex flex-col">
                 {/* Header */}
         <div className="flex items-center justify-between mb-3">
          <div className="flex-1">
            <div className="flex items-center space-x-2">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">{holding.symbol}</h3>
              <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                isPositive ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
              }`}>
                {isPositive ? 'Profitable' : 'Loss'}
              </div>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{holding.name}</p>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold text-gray-900 dark:text-white">
              {formatCurrency(calc.current_value)}
            </p>
            <div className="flex items-center justify-end space-x-1 mt-1">
              {isPositive ? (
                <ArrowUpRight className="h-4 w-4 text-green-600" />
              ) : (
                <ArrowDownRight className="h-4 w-4 text-red-600" />
              )}
              <p className={`text-sm font-semibold ${
                isPositive ? 'text-green-600' : 'text-red-600'
              }`}>
                {(calc.unrealized_gain_loss || 0) >= 0 ? '+' : ''}{formatCurrency(calc.unrealized_gain_loss)}
              </p>
            </div>
          </div>
        </div>

                                   {/* Stats Grid */}
          <div className="grid grid-cols-3 gap-2 mb-3 flex-1">
            <div className="text-center p-1.5 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
              <p className="text-xs text-gray-500 dark:text-gray-400 font-medium mb-0.5">Quantity</p>
              <p className="text-sm font-bold text-gray-900 dark:text-white">{holding.quantity}</p>
            </div>
            <div className="text-center p-1.5 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
              <p className="text-xs text-gray-500 dark:text-gray-400 font-medium mb-0.5">Avg Price</p>
              <p className="text-sm font-bold text-gray-900 dark:text-white">{formatCurrency(holding.average_price)}</p>
            </div>
            <div className="text-center p-1.5 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
              <p className="text-xs text-gray-500 dark:text-gray-400 font-medium mb-0.5">Current</p>
              <span className={`text-sm font-bold ${
                isTodayPositive ? 'text-green-600' : live.today_change < 0 ? 'text-red-600' : 'text-gray-900 dark:text-white'
              }`}>
                {formatCurrency(live.price)}
              </span>
            </div>
          </div>

                 {/* Today's Change */}
         <div className="flex items-center justify-between p-2 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg">
          <div className="flex items-center space-x-2">
            <Clock className="h-4 w-4 text-blue-600" />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Today's Change</span>
          </div>
          {live.today_change !== undefined && live.today_change !== null ? (
            <div className="flex items-center space-x-1">
              {isTodayPositive ? (
                <ArrowUpRight className="h-4 w-4 text-green-600" />
              ) : (
                <ArrowDownRight className="h-4 w-4 text-red-600" />
              )}
              <span className={`text-sm font-bold ${
                isTodayPositive ? 'text-green-600' : live.today_change < 0 ? 'text-red-600' : 'text-gray-900 dark:text-white'
              }`}>
                {live.today_change > 0 ? '+' : ''}{live.today_change?.toFixed(2)}
                {live.today_change_percent !== undefined && live.today_change_percent !== null ? 
                  ` (${live.today_change_percent > 0 ? '+' : ''}${live.today_change_percent?.toFixed(2)}%)` : ''
                }
              </span>
            </div>
          ) : (
            <span className="text-sm text-gray-500 dark:text-gray-400">N/A</span>
          )}
        </div>
      </div>
    </div>
  );
};

const Portfolio = () => {
  const [portfolio, setPortfolio] = useState(null);
  const [accountSummary, setAccountSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [liveHoldings, setLiveHoldings] = useState({});
  const [marketOpen, setMarketOpen] = useState(isUkMarketOpen());

  useEffect(() => {
    fetchPortfolioData();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setMarketOpen(isUkMarketOpen());
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  const fetchPortfolioData = async () => {
    try {
      setLoading(true);
      const [portfolioResponse, summaryResponse] = await Promise.all([
        api.get('/trading/portfolio/'),
        api.get('/trading/account/summary/')
      ]);

      if (portfolioResponse.data.success) {
        setPortfolio(portfolioResponse.data.data);
        setLiveHoldings({});
      }

      if (summaryResponse.data.success) {
        setAccountSummary(summaryResponse.data.data);
      }
    } catch (error) {
      console.error('Error fetching portfolio data:', error);
      setError('Failed to load portfolio data');
    } finally {
      setLoading(false);
    }
  };



  // Callback for HoldingCard to report live updates
  const handleLiveUpdate = (symbol, live, calc) => {
    setLiveHoldings(prev => {
      const updated = { ...prev, [symbol]: { ...live, ...calc } };
      // Only recalculate summary if market is open
      if (portfolio?.holdings && marketOpen) {
        const merged = portfolio.holdings.map(h => ({
          ...h,
          ...(updated[h.symbol] || {})
        }));
        const portfolio_value = merged.reduce((sum, h) => sum + (h.current_value || 0), 0);
        const todays_change = merged.reduce((sum, h) => sum + (h.today_change !== undefined ? h.today_change * h.quantity : 0), 0);
        const total_pnl = merged.reduce((sum, h) => sum + (h.unrealized_gain_loss || 0), 0);
        setAccountSummary(prevSummary => {
          const cash_balance = prevSummary?.cash_balance || 0;
          return {
            ...prevSummary,
            portfolio_value,
            todays_change,
            total_pnl,
            account_value: portfolio_value + cash_balance,
          };
        });
      }
      return updated;
    });
  };

  // const handleFundUpdate = (newAccountSummary) => {
  //   setAccountSummary(newAccountSummary);
  //   fetchPortfolioData();
  // };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '£0.00';
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount || 0);
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="mb-4">
            <AlertCircle className="h-16 w-16 text-red-500 mx-auto" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Error Loading Portfolio</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">{error}</p>
          <button 
            onClick={fetchPortfolioData}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 font-medium"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header Section */}
      <div className="relative">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Portfolio Overview</h1>
            <p className="text-gray-600 dark:text-gray-400 text-lg">
              Track your investments and monitor performance in real-time
            </p>
          </div>
        </div>
        
        {/* Market Status Banner */}
        {!marketOpen && (
          <div className="mt-4 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
            <div className="flex items-center space-x-2">
              <AlertCircle className="h-5 w-5 text-yellow-600" />
              <span className="text-yellow-800 dark:text-yellow-200 font-medium">
                Market is currently closed. Prices are not updating in real-time.
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Account Summary Cards */}
      {accountSummary && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Account Value */}
          <div className="relative overflow-hidden bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-6 text-white shadow-lg">
            <div className="absolute top-0 right-0 -mt-4 -mr-4 w-24 h-24 bg-white/10 rounded-full"></div>
            <div className="relative">
              <div className="flex items-center space-x-3 mb-3">
                <PoundSterling className="h-6 w-6" />
                <span className="text-blue-100 text-sm font-medium">Account Value</span>
              </div>
              <p className="text-3xl font-bold">{formatCurrency(accountSummary.account_value)}</p>
            </div>
          </div>

          {/* Today's Change */}
          <div className={`relative overflow-hidden rounded-xl p-6 text-white shadow-lg ${
            accountSummary.todays_change >= 0 
              ? 'bg-gradient-to-br from-green-500 to-green-600' 
              : 'bg-gradient-to-br from-red-500 to-red-600'
          }`}>
            <div className="absolute top-0 right-0 -mt-4 -mr-4 w-24 h-24 bg-white/10 rounded-full"></div>
            <div className="relative">
              <div className="flex items-center space-x-3 mb-3">
                {accountSummary.todays_change >= 0 ? (
                  <TrendingUp className="h-6 w-6" />
                ) : (
                  <TrendingDown className="h-6 w-6" />
                )}
                <span className="text-white/90 text-sm font-medium">Today's Change</span>
              </div>
              <p className="text-3xl font-bold">{formatCurrency(accountSummary.todays_change)}</p>
            </div>
          </div>

          {/* Total P&L */}
          <div className={`relative overflow-hidden rounded-xl p-6 text-white shadow-lg ${
            accountSummary.total_pnl >= 0 
              ? 'bg-gradient-to-br from-emerald-500 to-emerald-600' 
              : 'bg-gradient-to-br from-rose-500 to-rose-600'
          }`}>
            <div className="absolute top-0 right-0 -mt-4 -mr-4 w-24 h-24 bg-white/10 rounded-full"></div>
            <div className="relative">
              <div className="flex items-center space-x-3 mb-3">
                <BarChart3 className="h-6 w-6" />
                <span className="text-white/90 text-sm font-medium">Total P&L</span>
              </div>
              <p className="text-3xl font-bold">{formatCurrency(accountSummary.total_pnl)}</p>
            </div>
          </div>

          {/* Cash Balance */}
          <div className="relative overflow-hidden bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl p-6 text-white shadow-lg">
            <div className="absolute top-0 right-0 -mt-4 -mr-4 w-24 h-24 bg-white/10 rounded-full"></div>
            <div className="relative">
              <div className="flex items-center space-x-3 mb-3">
                <Wallet className="h-6 w-6" />
                <span className="text-white/90 text-sm font-medium">Cash Balance</span>
              </div>
              <p className="text-3xl font-bold">{formatCurrency(accountSummary.cash_balance)}</p>
            </div>
          </div>
        </div>
      )}

                    {/* Portfolio Holdings - Full Width */}
       <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
         <div className="p-6 border-b border-gray-200 dark:border-gray-700">
           <div className="flex items-center justify-between">
             <div>
               <h2 className="text-xl font-bold text-gray-900 dark:text-white">Portfolio Holdings</h2>
               <p className="text-gray-600 dark:text-gray-400 mt-1">
                 {portfolio?.holdings?.length || 0} positions • Live updates when market is open
               </p>
             </div>
             <div className="flex items-center space-x-2">
               <div className={`w-3 h-3 rounded-full ${marketOpen ? 'bg-green-500' : 'bg-red-500'}`}></div>
               <span className="text-sm text-gray-600 dark:text-gray-400">
                 {marketOpen ? 'Live' : 'Closed'}
               </span>
             </div>
           </div>
         </div>
         
         <div className="p-6">
                       {portfolio?.holdings && portfolio.holdings.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {portfolio.holdings.map((holding) => (
                  <HoldingCard key={holding.symbol} holding={holding} formatCurrency={formatCurrency} onLiveUpdate={handleLiveUpdate} />
                ))}
              </div>
           ) : (
             <div className="text-center py-12">
               <Target className="h-16 w-16 text-gray-400 mx-auto mb-4" />
               <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No Holdings Yet</h3>
               <p className="text-gray-600 dark:text-gray-400 mb-6">
                 Start trading to build your portfolio and see your positions here.
               </p>
               <button className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 font-medium">
                 Start Trading
               </button>
             </div>
           )}
         </div>
       </div>

       {/* Fund Management Sidebar - Commented Out */}
       {/* <div className="xl:col-span-1">
         <div className="sticky top-6">
           <FundManagement onFundUpdate={handleFundUpdate} />
         </div>
       </div> */}
    </div>
  );
};

export default Portfolio; 